import React from 'react';
import { RxCheck, RxCross1 } from 'react-icons/rx';

import {
  ComparisonTableProduct,
  ComparisonTableProps,
} from '@/app/_blocks/ComparisonTableGraphQL/types';

import classes from './ComparisonTable.module.scss';
import { DebugJsonBlock } from '../DebugJsonBlock';
import { Gutter } from '../Gutter';
import { SemanticHeading } from '../SemanticHeading';
import { renderTrademarkText } from '../TrademarkText';

export interface ComparisonTableEditorFeatureRowProps {
  rowId: string;
  label: string;
  columns: ComparisonTableProduct[];
  data: Record<string, Record<string, string>>;
}

const CellContent: React.FC<React.PropsWithChildren> = ({ children }) => {
  switch (children) {
    case 'YES':
      return <RxCheck />;
    case 'NO':
      return <RxCross1 />;
    default:
      return (
        <>
          {children}
        </>
      );
  }
};

export const ComparisonTableEditorFeatureRow = ({
  rowId,
  label,
  columns,
  data,
}: ComparisonTableEditorFeatureRowProps) => {
  return (
    <tr key={rowId}>
      <td>
        {renderTrademarkText(label)}
      </td>
      {columns.map(column => (
        <td key={column.id}>
          <CellContent>{data[column.id][rowId]}</CellContent>
        </td>
      ))}
    </tr>
  );
};
export const ComparisonTable: React.FC<{
  className?: string;
  comparisonTable: ComparisonTableProps;
}> = ({ className, comparisonTable }) => {
  const { headline, headline_legacy, table } = comparisonTable;
  const colWidth = 100 / (table.columns.length + 1);
  return (
    <Gutter className={[className, classes.wrapper].filter(Boolean).join(' ')}>
      <SemanticHeading 
        heading={headline}
        fallbackText={headline_legacy}
        fallbackTag="h2"
      />
      <div className={classes.tableContainer}>
        <div className={classes.fixedColumn}>
          <table className={classes.fixedTable}>
            <thead>
              <tr>
                <th scope="col"><span className="visually-hidden">Feature</span></th>
              </tr>
            </thead>
            <tbody>
              {table.rows.map(row => (
                <tr key={row.id}>
                  <td>{renderTrademarkText(row.label)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className={classes.scrollableColumns}>
          <table className={classes.scrollableTable}>
            <thead>
              <tr>
                {table.columns.map(column => (
                  <th key={column.id}>{renderTrademarkText(column.label)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map(row => (
                <tr key={row.id}>
                  {table.columns.map(column => (
                    <td key={column.id}>
                      <CellContent>{table.data[column.id][row.id]}</CellContent>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Desktop fallback table */}
        <table className={classes.desktopTable}>
          <thead>
            <tr>
              <th scope="col" style={{ width: `${colWidth}%` }}><span className="visually-hidden">Feature</span></th>
              {table.columns.map(column => (
                <th key={column.id} style={{ width: `${colWidth}%` }}>{renderTrademarkText(column.label)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map(row => (
              <ComparisonTableEditorFeatureRow
                key={row.id}
                rowId={row.id}
                label={row.label}
                columns={table.columns}
                data={table.data}
              />
            ))}
          </tbody>
        </table>
      </div>
      <DebugJsonBlock block={comparisonTable} />
    </Gutter>
  );
};

export default ComparisonTable;
